import React, { useState } from "react";
import {
  CANONICAL_STAGES,
  MODAL_TITLES,
  STAGE_DETAILS,
  STAGE_LABELS,
  modalLanguage,
  metricContextLabel,
  outreachCopy,
  projectContextLabel,
  type CanonicalStage,
  type QueueWorkflowItem,
} from "./gcRoiIntroductionWorkflow";

interface Props {
  item: QueueWorkflowItem;
  onClose: () => void;
  onPrimaryAction: (item: QueueWorkflowItem) => Promise<void> | void;
  onViewWorkflow: (item: QueueWorkflowItem) => void;
}

export default function GcRoiIntroductionActionModal({
  item,
  onClose,
  onPrimaryAction,
  onViewWorkflow,
}: Props) {
  const stage = (item.workflow_stage || "ADDED_TO_QUEUE") as CanonicalStage;
  const stageLabel = item.stage_label || STAGE_LABELS[stage] || stage;
  const title = MODAL_TITLES[stage] || "Introduction Action";
  const subtitle = STAGE_DETAILS[stage] || "";
  const language = modalLanguage(item.company_name || "Trade partner", stage);
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");
  const [busy, setBusy] = useState(false);
  const canAdvance = Boolean(item.permitted_next_status && item.primary_action_label);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(language);
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 1600);
    } catch {
      setCopyState("idle");
    }
  };

  const handlePrimary = async () => {
    if (!canAdvance || busy) return;
    setBusy(true);
    try {
      await onPrimaryAction(item);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="gc-roi-modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="gc-roi-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gc-roi-intro-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="gc-roi-modal-header">
          <div>
            <div id="gc-roi-intro-modal-title" className="gc-roi-modal-title">
              {title}
            </div>
            <div className="gc-roi-modal-subtitle">{subtitle}</div>
          </div>
          <button type="button" className="gc-roi-modal-close" onClick={onClose} aria-label="Close">
            {"\u00D7"}
          </button>
        </div>

        <div className="gc-roi-modal-meta">
          <div>
            <div className="gc-roi-meta-label">Current status</div>
            <div className="gc-roi-status-pill">{stageLabel}</div>
          </div>
          <div>
            <div className="gc-roi-meta-label">Trade partner</div>
            <div className="gc-roi-meta-value">{item.company_name}</div>
            <div className="gc-roi-meta-sub">
              {metricContextLabel(item.metric_key)} {"\u00B7"} {projectContextLabel(item)}
            </div>
          </div>
          <div>
            <div className="gc-roi-meta-label">Current owner</div>
            <div className="gc-roi-owner-pill">{item.owner || "Unassigned"}</div>
          </div>
          <div>
            <div className="gc-roi-meta-label">Outreach</div>
            <div className="gc-roi-meta-value">{outreachCopy(Boolean(item.outreach_occurred))}</div>
          </div>
        </div>

        <div className="gc-roi-modal-language">
          <div className="gc-roi-meta-label">Message / record language</div>
          <pre className="gc-roi-language-block">{language}</pre>
        </div>

        <div className="gc-roi-modal-actions">
          <button type="button" className="gc-roi-btn-secondary" onClick={handleCopy}>
            {copyState === "copied" ? "Copied" : "Copy language"}
          </button>
          <button
            type="button"
            className="gc-roi-btn-secondary"
            onClick={() => onViewWorkflow(item)}
          >
            View full workflow
          </button>
          {canAdvance ? (
            <button
              type="button"
              className="gc-roi-btn-primary"
              disabled={busy}
              onClick={handlePrimary}
            >
              {item.primary_action_label}
            </button>
          ) : (
            <button type="button" className="gc-roi-btn-primary" onClick={onClose}>
              Close
            </button>
          )}
        </div>

        <div className="gc-roi-modal-stage-hint">
          Stage {CANONICAL_STAGES.indexOf(stage) + 1} of {CANONICAL_STAGES.length} {"\u00B7"} Transitions
          require the explicit primary action only
        </div>
      </div>
    </div>
  );
}
